import styled from '@emotion/styled';
import Paper from '@mui/material/Paper';
import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useWordpress from '../../hooks/useWordpress';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import { Center } from './StepBookingCenter';
import { Country } from './StepBookingCountry';
import { Certification } from './StepCertification';
import { Language } from './StepCertificationLanguage';
import { Method, OPTIONS } from './StepCertificationMethod';

const StepBookingSummaryPaper = styled(Paper)`
  padding: 12px;
  margin-left: -45px;
`;
const StepBookingSummaryTable = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
`;
const StepBookingSummaryRow = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
`;
const StepBookingSummaryItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  flex-basis: 0;
  padding: 2px 8px;
`;
const StepBookingSummaryItemBold = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  flex-basis: 0;
  padding: 2px 8px;
  font-weight: 700;
`;

const StepBookingSummary: FunctionComponent<{
  language: Language;
  certification: Certification;
  country: Country;
  center: Center;
  method: Method;
}> = ({ language, certification, country, center, method }) => {
  const { isLoading, response } = useWordpress<
    | string
    | {
        success: number;
        price: number;
        formattedPrice: number;
        currencyCode: string;
        baseCurrencyId: number;
        country: string;
        fullyFormatted: string;
      }
  >(`/certifications/v1/price`, {
    exam_module_id: certification.exillenceId,
    exam_service_id: center.value,
    exam_country_id: country.value,
  });

  if (isLoading) {
    return <ExinMuiCircularProgress />;
  }

  if (!response) {
    return <Trans i18nKey="messageNoBookingSummary">Summary not available</Trans>;
  }
  if (typeof response === 'string') {
    // The API returns request errors as HTTP 200 and an XML string.
    return (
      <Trans i18nKey="messageErrorBookingSummary">
        Something went wrong while getting the summary information.
      </Trans>
    );
  }

  return (
    <StepBookingSummaryPaper>
      <StepBookingSummaryTable>
        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryCertificationLabel">Certification</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItem>{certification.label}</StepBookingSummaryItem>
        </StepBookingSummaryRow>

        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryCertificationLanguageLabel">Language</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItem>{language.label}</StepBookingSummaryItem>
        </StepBookingSummaryRow>

        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryCertificationMethodLabel">Method</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItem>{OPTIONS[method]?.label}</StepBookingSummaryItem>
        </StepBookingSummaryRow>

        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryBookingCountryLabel">Country</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItem>{response.country}</StepBookingSummaryItem>
        </StepBookingSummaryRow>

        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryBookingCenterLabel">Test center</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItem>{center.label}</StepBookingSummaryItem>
        </StepBookingSummaryRow>

        <StepBookingSummaryRow>
          <StepBookingSummaryItemBold>
            <Trans i18nKey="bookingSummaryPriceLabel">Price</Trans>
          </StepBookingSummaryItemBold>
          <StepBookingSummaryItemBold
            // @NOTE potentially executes malicious javascript;
            dangerouslySetInnerHTML={{ __html: response.fullyFormatted }}
          />
        </StepBookingSummaryRow>
      </StepBookingSummaryTable>
    </StepBookingSummaryPaper>
  );
};

export default StepBookingSummary;
